import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/products", label: "Our Products" },
  { to: "/services", label: "Our Services" },
  { to: "/contact", label: "Contact Us" },
];

export const Logo = ({ light = false }) =>
  light ? (
    <img
      src="/assets/das-logo.jpg"
      alt="DAS — Dynamic App Solutions"
      className="h-14 w-auto rounded-xl bg-white p-1.5"
    />
  ) : (
    <img
      src="/assets/das-mark.jpg"
      alt="DAS — Dynamic App Solutions"
      className="h-12 w-auto mix-blend-multiply"
    />
  );

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled ? "border-b border-black/5 bg-white/70 backdrop-blur-xl" : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 md:px-12">
          <Link to="/" data-testid="nav-logo" aria-label="DAS home">
            <Logo />
          </Link>

          <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
            {links.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `relative rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-300 ${
                    isActive ? "text-das-deep" : "text-neutral-600 hover:text-das-ink"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {link.label}
                    {isActive && (
                      <motion.span
                        layoutId="nav-active-pill"
                        className="absolute inset-0 -z-10 rounded-full bg-das-violet/10"
                        transition={{ type: "spring", stiffness: 380, damping: 32 }}
                      />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/consultation"
              data-testid="nav-book-consultation-button"
              className="group hidden items-center gap-2 rounded-full bg-das-violet px-6 py-3 font-display text-sm font-semibold text-white transition-all duration-300 hover:bg-das-deep hover:shadow-lift sm:inline-flex"
            >
              Book a Consultation
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <button
              onClick={() => setOpen(true)}
              data-testid="nav-mobile-menu-button"
              aria-label="Open menu"
              className="grid h-11 w-11 place-items-center rounded-full border border-neutral-200 bg-white text-das-ink lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="nav-mobile-menu"
            className="fixed inset-0 z-[70] flex flex-col bg-das-ink text-white"
          >
            <div className="flex h-20 items-center justify-between px-6">
              <Logo light />
              <button
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-close-button"
                aria-label="Close menu"
                className="grid h-11 w-11 place-items-center rounded-full border border-white/20"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8" aria-label="Mobile">
              {links.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, y: 28 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={link.to}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `block py-2 font-display text-4xl font-semibold tracking-tight transition-colors ${
                        isActive ? "text-das-violet" : "text-white/85 hover:text-white"
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45, duration: 0.5 }}
                className="pt-6"
              >
                <Link
                  to="/consultation"
                  data-testid="mobile-nav-book-consultation-button"
                  className="inline-flex items-center gap-2 rounded-full bg-das-violet px-8 py-4 font-display text-base font-semibold text-white"
                >
                  Book a Consultation
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </motion.div>
            </nav>
            <p className="px-8 pb-8 text-xs uppercase tracking-[0.2em] text-white/40">
              Johannesburg · Gauteng · Botswana
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
